import { useState, useEffect, lazy, Suspense } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Target, GraduationCap, Clock, ClipboardList, Zap, Lightbulb, NotebookPen, CheckCircle2, Flame, Sprout } from 'lucide-react';
import { getStoredSession } from '../hooks/useAuth';
import { isPracticeTestEnabled } from '../config/features';
import { supabase } from '../services/supabaseClient';
import { InstallAppButton } from '../components/InstallAppButton';
import { getStudentAssignments, type StudentAssignment } from '../services/assignmentService';
import { getPendingCheckins, type PendingCheckin } from '../services/checkinService';
import { StudentWelcomeName, type HomeStats } from '../components/StudentWelcomeName';
import { Card } from '@branon-shared/ui/Card';
import { IconBadge } from '@branon-shared/ui/IconBadge';
import { ChinniMascot } from '@branon-shared/ui/ChinniMascot';
import { TopTearCard } from '@branon-shared/ui/TopTearCard';
import { SpillingDoodlesCompact } from '@branon-shared/ui/Doodle';

// DoubtChatbotTemplate doesn't actually use conceptId/conceptName in its
// logic (ChinniTemplate + free-text topic fill-ins are concept-agnostic) --
// only ConceptPage.tsx passed real values, out of historical convention.
// Mounting it here directly, skipping the old concept-picker detour, lets
// "Ask Chinni" open the same working chatbot immediately from the dashboard.
const DoubtChatbotTemplate = lazy(() =>
  import('../components/DoubtChatbotTemplate').then((m) => ({ default: m.DoubtChatbotTemplate }))
);

// Phase 8 (redesign port): hide, don't delete -- the old streak/weekly-stats/
// overconfidence-banner layout stays in the file (flip back to `true` to see
// it), just not rendered by default, matching rewise-ui's own Phase 4 idiom.
const SHOW_LEGACY_STATS = false;

// Phase 8 (Exam Prep orchestration port): superseded by the /exam-prep-plan
// entry point. Hidden, not deleted (same idiom as SHOW_LEGACY_STATS above) --
// this card was already live and labeled "Exam Prep," just pointing at the
// flat Practice Test/Simulator flow.
const SHOW_LEGACY_EXAM_SIMULATOR_CARD = false;

interface MasteryStats {
  streak: number;
  questionsThisWeek: number;
  accuracyThisWeek: number | null;
  conceptsExplored: number;
  bestWeekQuestions: number;
}

interface ReviewItem {
  id: number;
  name: string;
  daysSince: number;
}

export default function Dashboard() {
  const session = getStoredSession();
  const [masteryStats, setMasteryStats] = useState<MasteryStats>({
    streak: 0, questionsThisWeek: 0, accuracyThisWeek: null, conceptsExplored: 0, bestWeekQuestions: 0,
  });
  const [reviewDue, setReviewDue] = useState<ReviewItem[]>([]);
  const [loadingMastery, setLoadingMastery] = useState(true);
  const [assignments, setAssignments] = useState<StudentAssignment[]>([]);
  const [loadingAssignments, setLoadingAssignments] = useState(true);
  const [overconfidentConcept, setOverconfidentConcept] = useState<{ id: number; name: string } | null>(null);
  const [homeStats, setHomeStats] = useState<HomeStats | null>(null);
  const [isChinniOpen, setIsChinniOpen] = useState(false);
  const [pendingCheckins, setPendingCheckins] = useState<PendingCheckin[]>([]);

  useEffect(() => {
    if (!session?.student_id) return;
    const studentId = session.student_id;
    loadMasteryData(studentId);
    getPendingCheckins(studentId).then(setPendingCheckins).catch(() => setPendingCheckins([]));
    setLoadingAssignments(true);
    getStudentAssignments(studentId)
      .then(setAssignments)
      .catch(() => setAssignments([]))
      .finally(() => setLoadingAssignments(false));
  }, [session?.student_id]);

  async function loadMasteryData(studentId: string) {
    try {
      const { data, error } = await supabase.rpc('get_student_home_stats', {
        p_student_id: studentId,
      });
      if (error || !data) return;

      setMasteryStats({
        streak: data.streak ?? 0,
        questionsThisWeek: data.questionsThisWeek ?? 0,
        accuracyThisWeek: data.accuracyThisWeek ?? null,
        conceptsExplored: data.conceptsExplored ?? 0,
        bestWeekQuestions: data.bestWeekQuestions ?? 0,
      });

      setHomeStats({
        streak: data.streak ?? 0,
        weeklyXP: data.weeklyXP ?? 0,
        todayConcepts: data.todayConcepts ?? 0,
        daysSinceLastEvent: data.daysSinceLastEvent ?? 0,
        lastConceptId: data.lastConceptId ?? null,
        lastConceptName: data.lastConceptName ?? null,
      });

      if (data.overconfidentConceptId && data.overconfidentConceptName) {
        setOverconfidentConcept({ id: data.overconfidentConceptId, name: data.overconfidentConceptName });
      }

      if (Array.isArray(data.reviewDue)) {
        setReviewDue(data.reviewDue as ReviewItem[]);
      }
    } finally {
      setLoadingMastery(false);
    }
  }

  const isPersonalBest =
    masteryStats.questionsThisWeek > 0 &&
    masteryStats.questionsThisWeek >= masteryStats.bestWeekQuestions &&
    masteryStats.bestWeekQuestions > 0;

  return (
    <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-6 sm:py-8 space-y-5">

      {/* Welcome card, with the intent-first entry points revealed inside its torn edge */}
      {session?.student_id ? (
        <StudentWelcomeName studentId={session.student_id} homeStats={homeStats ?? undefined} showGamification={false}>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Card className="hover:shadow-none transition-shadow">
              <Link to="/understand-something" className="block">
                <IconBadge icon={Lightbulb} tone="marigold" rotate={-6} />
                <h3 className="text-base font-bold mt-3" style={{ color: '#171512' }}>Understand Something</h3>
                <p className="text-sm text-gray-600 mt-1">Explain it, work through it, get comfortable</p>
              </Link>
            </Card>

            <Card className="hover:shadow-none transition-shadow">
              <Link to="/prepare-for-test" className="block">
                <IconBadge icon={NotebookPen} tone="coral" rotate={4} />
                <h3 className="text-base font-bold mt-3" style={{ color: '#171512' }}>Prepare for a Test</h3>
                <p className="text-sm text-gray-600 mt-1">Got a test coming? Get ready for it</p>
              </Link>
            </Card>

            <Card className="hover:shadow-none transition-shadow">
              <button onClick={() => setIsChinniOpen(true)} className="text-left block w-full">
                <ChinniMascot size={44} />
                <h3 className="text-base font-bold mt-3" style={{ color: '#171512' }}>Ask Chinni</h3>
                <p className="text-sm text-gray-600 mt-1">Quick question? Just ask.</p>
              </button>
            </Card>
          </div>
        </StudentWelcomeName>
      ) : (
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-quicksand text-xl sm:text-2xl font-bold text-ink">Welcome back!</h1>
            <p className="text-gray-600 text-sm mt-1">JEE/NEET Prep</p>
          </div>
          <div className="md:hidden">
            <InstallAppButton />
          </div>
        </div>
      )}

      <Suspense fallback={null}>
        <DoubtChatbotTemplate externalOpen={isChinniOpen} onExternalOpenChange={setIsChinniOpen} />
      </Suspense>

      {/* Overconfidence nudge — hidden, not deleted (SHOW_LEGACY_STATS) */}
      {SHOW_LEGACY_STATS && overconfidentConcept && (
        <div className="flex items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
          <p className="text-sm text-amber-800">
            <span className="font-semibold">Heads up:</span> You were confident about{' '}
            <span className="font-semibold">{overconfidentConcept.name}</span> but got it wrong. Give it another look.
          </p>
          <Link
            to={`/concept/${overconfidentConcept.id}`}
            className="flex-shrink-0 text-xs font-semibold text-amber-700 underline hover:text-amber-900"
          >
            Review →
          </Link>
        </div>
      )}

      {/* This Week stats — hidden, not deleted (SHOW_LEGACY_STATS) */}
      {SHOW_LEGACY_STATS && (
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-bold" style={{ color: '#171512' }}>This Week</h2>
          {masteryStats.conceptsExplored > 0 && (
            <span className="text-xs text-gray-400 font-medium">{masteryStats.conceptsExplored} concepts explored total</span>
          )}
        </div>

        {loadingMastery ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[0, 1, 2, 3].map(i => <div key={i} className="h-20 bg-gray-100 rounded-xl animate-pulse" />)}
          </div>
        ) : (
          <>
            {isPersonalBest && (
              <p className="text-xs text-coral font-semibold mb-3">Personal best week!</p>
            )}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="text-center p-4 bg-orange-50 rounded-xl">
                {masteryStats.streak > 0 ? (
                  <>
                    <div className="text-3xl font-extrabold text-coral mb-1">{masteryStats.streak}</div>
                    <p className="text-xs font-medium text-gray-600 flex items-center justify-center gap-1"><Flame className="w-3.5 h-3.5" /> Day streak</p>
                  </>
                ) : (
                  <>
                    <Sprout className="w-6 h-6 mx-auto mb-1 text-gray-400" />
                    <p className="text-xs font-medium text-gray-500">Start your streak</p>
                  </>
                )}
              </div>
              <div className="text-center p-4 bg-orange-50 rounded-xl">
                <div className="text-3xl font-extrabold text-coral mb-1">{masteryStats.questionsThisWeek}</div>
                <p className="text-xs font-medium text-gray-600">Questions answered</p>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-xl">
                {masteryStats.accuracyThisWeek !== null ? (
                  <>
                    <div className="text-3xl font-extrabold text-green-600 mb-1">{masteryStats.accuracyThisWeek}%</div>
                    <p className="text-xs font-medium text-gray-600">Accuracy</p>
                  </>
                ) : (
                  <>
                    <Target className="w-6 h-6 mx-auto mb-1 text-gray-400" />
                    <p className="text-xs font-medium text-gray-400">Answer 3+ to see</p>
                  </>
                )}
              </div>
              <div className="text-center p-4 bg-purple-50 rounded-xl">
                <div className="text-3xl font-extrabold text-purple-600 mb-1">{masteryStats.conceptsExplored}</div>
                <p className="text-xs font-medium text-gray-600">Concepts explored</p>
              </div>
            </div>
            {masteryStats.questionsThisWeek === 0 && (
              <p className="text-center text-xs text-gray-400 mt-3">Answer some questions to see your weekly stats!</p>
            )}
          </>
        )}
      </Card>
      )}

      {/* A quick check-in from home — soft, never assignment-toned copy.
          Full-width card -> tears from the top (see redesignplan.md §9/10). */}
      {pendingCheckins.length > 0 && (
        <TopTearCard icon={<Clock size={20} />} title="A quick check-in from home" subtitle="Whenever you have a minute" tone="coral" spill={<SpillingDoodlesCompact />}>
          <div className="space-y-2">
            {pendingCheckins.map(item => (
              <Link
                key={item.id}
                to={`/check-in/${item.id}`}
                className="flex items-center justify-between p-3 bg-gray-50 border-2 border-gray-100 rounded-btn hover:border-coral transition-colors group"
              >
                <p className="text-sm font-semibold group-hover:text-coral transition-colors" style={{ color: '#171512' }}>{item.concept_name}</p>
                <span className="text-xs font-semibold text-coral bg-white border border-gray-200 px-2.5 py-1 rounded-full">
                  Check In →
                </span>
              </Link>
            ))}
          </div>
        </TopTearCard>
      )}

      {/* Worth a quick review (was "Due for Review") — same data, full-width -> tears from the top. */}
      {!loadingMastery && reviewDue.length > 0 && (
        <TopTearCard icon={<Clock size={20} />} title="Worth a quick review" subtitle="Keep it fresh — quick wins waiting" tone="marigold" spill={<SpillingDoodlesCompact />}>
          <div className="space-y-2">
            {reviewDue.map(item => (
              <Link
                key={item.id}
                to={`/concept/${item.id}`}
                className="flex items-center justify-between p-3 bg-gray-50 border-2 border-gray-100 rounded-btn hover:border-coral transition-colors group"
              >
                <div>
                  <p className="text-sm font-semibold group-hover:text-coral transition-colors" style={{ color: '#171512' }}>{item.name}</p>
                  <p className="text-xs text-gray-400 mt-0.5">Last practiced {item.daysSince} day{item.daysSince !== 1 ? 's' : ''} ago</p>
                </div>
                <span className="text-xs font-semibold text-coral bg-white border border-gray-200 px-2.5 py-1 rounded-full">
                  Review →
                </span>
              </Link>
            ))}
          </div>
        </TopTearCard>
      )}

      {/* Assignments — full-width card -> tears from the top. */}
      {!loadingAssignments && assignments.length > 0 && (
        <TopTearCard icon={<ClipboardList size={20} />} title="My Assignments" tone="night" spill={<SpillingDoodlesCompact />}>
          <div className="space-y-3">
            {assignments.map((assignment) => {
              const dueDate = new Date(assignment.due_date);
              const isOverdue = dueDate < new Date() && assignment.status === 'pending';
              const daysUntilDue = Math.ceil((dueDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
              return (
                <div key={assignment.assignment_id} className={`rounded-btn p-4 border-2 ${
                  assignment.status === 'completed' ? 'bg-success-soft border-success' :
                  isOverdue ? 'bg-critical-soft border-critical' : 'bg-gray-50 border-gray-200'
                }`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h3 className="text-sm font-bold" style={{ color: '#171512' }}>{assignment.title}</h3>
                        <span className="px-2 py-0.5 bg-white rounded-full text-xs font-semibold text-gray-700 border border-gray-200 capitalize">{assignment.type}</span>
                        {assignment.status === 'completed' && (
                          <span className="px-2 py-0.5 bg-success text-white rounded-full text-xs font-semibold flex items-center gap-1">
                            <CheckCircle2 className="w-3 h-3" /> Done
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-700 font-medium">{assignment.concept_name}</p>
                      {assignment.description && (
                        <p className="text-xs text-gray-600 mt-0.5">{assignment.description}</p>
                      )}
                      <div className="flex items-center gap-1 text-xs text-gray-500 mt-1">
                        <Clock className="w-3 h-3" />
                        <span>
                          Due {dueDate.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                          {assignment.status === 'pending' && (
                            <span className={isOverdue ? 'text-critical font-bold ml-1' : 'ml-1'}>
                              {isOverdue ? '(Overdue!)' : daysUntilDue === 0 ? '(Today)' : daysUntilDue === 1 ? '(Tomorrow)' : `(${daysUntilDue} days)`}
                            </span>
                          )}
                        </span>
                      </div>
                      {assignment.status === 'completed' && assignment.score !== undefined && (
                        <p className="text-xs text-success font-bold mt-1">Score: {assignment.score.toFixed(0)}%</p>
                      )}
                    </div>
                    <div>
                      {assignment.status === 'pending' ? (
                        <Link
                          to={`/concept/${assignment.concept_id}`}
                          state={{ assignment: { id: assignment.assignment_id, type: assignment.type, due_date: assignment.due_date, title: assignment.title } }}
                          className="px-3 py-2 bg-coral text-white rounded-btn font-bold text-xs shadow-sticker hover:opacity-90 transition-opacity inline-flex items-center gap-1"
                        >
                          <Zap className="w-3 h-3" /> Start
                        </Link>
                      ) : (
                        <span className="text-success font-bold text-xs">
                          Completed on {new Date(assignment.completed_at!).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </TopTearCard>
      )}

      {/* My Progress */}
      <Link to="/analytics" className="block">
        <Card className="hover:shadow-none transition-shadow">
          <div className="flex items-center gap-3 mb-2 sm:mb-3">
            <IconBadge icon={Target} tone="night" />
            <div>
              <h3 className="text-base sm:text-lg font-bold" style={{ color: '#171512' }}>My Progress</h3>
              <p className="text-xs text-coral font-semibold">View detailed analytics</p>
            </div>
          </div>
          <p className="text-sm text-gray-600">Track your learning journey, test scores, and improvement over time</p>
        </Card>
      </Link>

      {/* Browse Concepts + Exam Prep */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Link to="/concepts" className="block">
          <Card className="h-full">
            <div className="flex items-center gap-3">
              <IconBadge icon={BookOpen} tone="coral" rotate={-4} />
              <div>
                <p className="font-bold" style={{ color: '#171512' }}>Browse Concepts</p>
                <p className="text-sm text-gray-500">Explore topics</p>
              </div>
            </div>
          </Card>
        </Link>
        {session && isPracticeTestEnabled(session.grade_level, session.board) && (
          <Link to="/exam-prep-plan" className="block">
            <Card className="h-full">
              <div className="flex items-center gap-3">
                <IconBadge icon={GraduationCap} tone="marigold" />
                <div>
                  <p className="font-bold" style={{ color: '#171512' }}>Exam Prep</p>
                  <p className="text-sm text-gray-500">Practice for exams</p>
                </div>
              </div>
            </Card>
          </Link>
        )}
      </div>

      {/* Exam Prep (legacy, flat /practice-test link) — hidden, not deleted (SHOW_LEGACY_EXAM_SIMULATOR_CARD) */}
      {SHOW_LEGACY_EXAM_SIMULATOR_CARD && (
        <Link to="/practice-test" className="block">
          <Card className="h-full">
            <div className="flex items-center gap-3">
              <IconBadge icon={GraduationCap} tone="marigold" />
              <div>
                <p className="font-bold" style={{ color: '#171512' }}>Exam Prep</p>
                <p className="text-sm text-gray-500">Practice for exams</p>
              </div>
            </div>
          </Card>
        </Link>
      )}

    </main>
  );
}
